import { EllipsisVerticalIcon } from "@heroicons/react/24/outline";
import { isReviewItemResolved } from "@utils/reportVisibility";
import { PropsWithChildren, ReactNode, useState } from "react";

import { Small } from "@fronterahealth/frontera-ui-components";
import { cn } from "@fronterahealth/frontera-ui-components/lib/utils";
import { Button } from "@fronterahealth/frontera-ui-components/ui/button";
import { Card, CardContent, CardHeader } from "@fronterahealth/frontera-ui-components/ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@fronterahealth/frontera-ui-components/ui/dropdown-menu";
import {
  Item,
  ItemActions,
  ItemContent,
  ItemDescription,
  ItemTitle,
} from "@fronterahealth/frontera-ui-components/ui/item";

import { ReviewItemFieldsFragment } from "@api/graphql/types-and-hooks";
import { ProposedEditDiff } from "@components/QaReview/PeerReview/ProposedEditDiff";
import { AuthorAvatar } from "@components/QaReview/components/AuthorAvatar";
import { AuthorName } from "@components/QaReview/components/AuthorName";
import {
  CommentActionDeleteDialog,
  CommentActionDeleteItem,
} from "@components/QaReview/components/CommentActionDelete";
import { CommentActionEdit } from "@components/QaReview/components/CommentActionEdit";
import { CommentBody } from "@components/QaReview/components/CommentBody";
import { ResolutionLabel } from "@components/QaReview/components/ResolutionLabel";
import { SourceBadge } from "@components/QaReview/components/SourceBadge";
import { Timestamp } from "@components/QaReview/components/Timestamp";
import { UNNAMED_FIELD, commentFieldTitle } from "@components/QaReview/hooks/useReviewAnchors";
import { useReviewFieldType } from "@components/QaReview/hooks/useReviewFieldType";
import { useReviewSession } from "@components/QaReview/hooks/useReviewSession";

export interface CommentMessageProps {
  comment: ReviewItemFieldsFragment;
  depth: 0 | 1;
  isActive?: boolean;
  /** Goes to the item's field; a resolved summary calls it when opened or collapsed, as clicking the card does. */
  onActivate?: () => void;
  /** The card's top-right control on an open root card, next to the author name. */
  headerAction?: ReactNode;
}

export const CommentActionMenu: React.FC<{ comment: ReviewItemFieldsFragment }> = ({ comment }) => {
  const { state } = useReviewSession();
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  // Edit and Delete are the author's own actions, hidden while the comment is being edited.
  const isComment = comment.__typename === "ReportCommentType" && state.editingCommentId !== comment.id;
  const canEdit = isComment && !!comment.canEdit;
  const canDelete = isComment && !!comment.canDelete;

  if (!canEdit && !canDelete && !confirmingDelete) return null;

  return (
    <ItemActions className="shrink-0 self-start">
      <DropdownMenu>
        <DropdownMenuTrigger
          render={
            <Button variant="ghost" size="icon-xs" aria-label="More actions">
              <EllipsisVerticalIcon />
            </Button>
          }
        />
        <DropdownMenuContent align="end">
          {canEdit && <CommentActionEdit comment={comment} />}
          {canDelete && <CommentActionDeleteItem onOpen={() => setConfirmingDelete(true)} />}
        </DropdownMenuContent>
      </DropdownMenu>
      {(canDelete || confirmingDelete) && (
        <CommentActionDeleteDialog comment={comment} open={confirmingDelete} onOpenChange={setConfirmingDelete} />
      )}
    </ItemActions>
  );
};

const FieldProposedEditDiff: React.FC<{ fieldId?: string; baseValue: string; proposedValue: string }> = ({
  fieldId,
  baseValue,
  proposedValue,
}) => <ProposedEditDiff fieldType={useReviewFieldType(fieldId)} baseValue={baseValue} proposedValue={proposedValue} />;

export const CommentMessage: React.FC<PropsWithChildren<CommentMessageProps>> = ({
  comment,
  depth,
  isActive = false,
  headerAction,
  children,
}) => {
  const resolved = isReviewItemResolved(comment);
  // A deleted root stays only to hold its replies: one gray line stands in for its author and text.
  const deleted = comment.__typename === "ReportCommentType" && comment.isDeleted;
  const showResolution = depth === 0 && resolved;
  const fieldTitle = commentFieldTitle(comment);
  const proposedEdit =
    comment.__typename === "ReportCommentType" && comment.baseValue != null && comment.proposedValue != null
      ? { baseValue: comment.baseValue, proposedValue: comment.proposedValue }
      : null;

  return (
    <Card size="sm" className={cn("transition-all duration-150", isActive && "shadow-xl -translate-x-2")}>
      <CardHeader>
        <Item size="xs" className="min-w-0 flex-nowrap border-0 p-0">
          {!deleted && <AuthorAvatar comment={comment} />}
          {/* min-w-0 + truncate: long names shorten with "…" so the menu and timestamp stay on the card. */}
          <ItemContent className="min-w-0">
            {/* The resolution label sits right of the name when it fits and wraps under it when it doesn't. */}
            <div className="flex flex-wrap items-baseline gap-x-1.5">
              {deleted ? (
                <Small className="text-text-tertiary">Comment deleted</Small>
              ) : (
                <ItemTitle className="max-w-full min-w-0 items-baseline gap-1.5">
                  <span className="min-w-0 [&>p]:truncate">
                    <AuthorName comment={comment} />
                  </span>
                  {comment.__typename === "ReportCommentType" && (
                    <Small className="shrink-0 whitespace-nowrap text-text-tertiary">
                      <Timestamp value={comment.createdDate} />
                    </Small>
                  )}
                </ItemTitle>
              )}
              {showResolution && (
                <Small className="ml-auto max-w-full truncate font-medium text-emerald-700">
                  <ResolutionLabel comment={comment} />
                </Small>
              )}
            </div>
            {depth === 0 && (
              <div className="mt-0.5 flex min-w-0 flex-wrap items-center gap-x-1.5">
                <SourceBadge comment={comment} />
                <ItemDescription
                  className="line-clamp-1 min-w-0"
                  title={fieldTitle || undefined}
                  data-dd-action-name="comment field label"
                >
                  {fieldTitle ? `on “${fieldTitle}”` : `on ${UNNAMED_FIELD}`}
                </ItemDescription>
              </div>
            )}
          </ItemContent>
          {depth === 0 && !resolved && headerAction && (
            <ItemActions className="shrink-0 self-start">{headerAction}</ItemActions>
          )}
          <CommentActionMenu comment={comment} />
        </Item>
      </CardHeader>

      {!deleted && (
        <CardContent className="space-y-2">
          <CommentBody comment={comment} isActive={isActive} />

          {proposedEdit && !resolved && (
            <div className="[&_span]:pointer-events-auto" data-dd-action-name="suggested edit">
              <FieldProposedEditDiff
                fieldId={comment.reportSectionField?.id}
                baseValue={proposedEdit.baseValue}
                proposedValue={proposedEdit.proposedValue}
              />
            </div>
          )}
        </CardContent>
      )}

      {/* Rows below the body (action row, replies, reply box). Not a CardFooter: that slot assumes it
          comes last and drops the card's bottom padding. */}
      {children}
    </Card>
  );
};
